import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BaselineResponse, BulkImportType } from '@dr-customer-offers-ui/lib-interfaces';
import { BaselineService } from '@dr-customer-offers-ui/lib-services';
import { Store } from '@ngrx/store';
import { DataModel } from '@ngx-interval-data-grid';
import { TranslateService } from '@ngx-translate/core';
import { exportFile } from 'common-utils/dist/csv-export';
import moment from 'moment';
import 'moment-timezone';
import { catchError, map, Observable, of } from 'rxjs';
import { AppState } from '../../store';
import * as Selectors from '../../store/selectors/com.selectors';
import { BaselineGateClosureInfoResult, CsvTemplateConfig, OffersCsvTemplateConfig } from '../models/csv-template.model';
import { DataModelService } from './data-model.service';
import { DateService } from './date.service';

@Injectable({ providedIn: 'root' })
export class GenerateCsvTemplateService {
  protected currentRegViewConfig = this.store.selectSignal(Selectors.selectCurrentRegViewConfig);

  private readonly columnLabelKeys: Readonly<Record<string, string>> = Object.freeze({
    registration_id: 'COM.CSV.CSV_REG_ID',
    registration_name: 'COM.CSV.CSV_REG_NAME',
    start_dttm: 'COM.CSV.CSV_INTERVAL_START',
    end_dttm: 'COM.CSV.CSV_INTERVAL_END',
    opt_out: 'COM.CSV.CSV_OPT_OUT',
    kW: 'COM.CSV.CSV_OFFER_VALUE',
    KW_TO_INCREASE: 'COM.CSV.CSV_KW_TO_INCREASE',
    KW_TO_DECREASE: 'COM.CSV.CSV_KW_TO_DECREASE',
    PRICE_TO_INCREASE: 'COM.CSV.CSV_PRICE_TO_INCREASE',
    PRICE_TO_DECREASE: 'COM.CSV.CSV_PRICE_TO_DECREASE',
    DEMAND_FORECAST: 'COM.CSV.CSV_DEMAND_FORECAST',
    price: 'COM.CSV.CSV_PRICE',
    quantity: 'COM.CSV.CSV_QUANTITY',
    baseline_value: 'COM.CSV.CSV_BASELINE_VALUE'
  });

  constructor(
    private store: Store<AppState>,
    private baselineService: BaselineService,
    private dateService: DateService,
    private dataModelService: DataModelService,
    private destroyRef: DestroyRef,
    private router: Router,
    private translate: TranslateService
  ) {}

  private buildLabelRow(keyRow: string[]): string[] {
    return keyRow.map((key) => {
      const i18nKey = this.columnLabelKeys[key];
      if (!i18nKey) return this.sanitizeCsvCell(key);
      const label = this.translate.instant(i18nKey);
      return this.sanitizeCsvCell(label === i18nKey ? key : label);
    });
  }

  private sanitizeCsvCell(value: unknown): string {
    const s = value == null ? '' : String(value);
    return s.replace(/[",\r\n]+/g, '-');
  }

  /**
   * Generates and downloads the selected CSV template.
   *
   * Offers CSV is generated synchronously because it does not need extra data.
   * Baselines CSV is generated asynchronously because it first loads baseline
   * data for the selected CSV date range to apply gate-closure restrictions.
   */
  generateAndDownload(config: CsvTemplateConfig): void {
    if (config.importType === BulkImportType.Offers) {
      const csvContent = this.buildOffersCsv(config);
      exportFile(csvContent, this.buildFileName(config.registrationId, config.importType));
      return;
    }

    this.buildBaselinesCsv$(config)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((csvContent) => {
        exportFile(csvContent, this.buildFileName(config.registrationId, config.importType));
      });
  }

  // ------------------------------------
  // OFFERS
  // ------------------------------------

  private buildOffersCsv(config: OffersCsvTemplateConfig): string {
    const isCleared = this.router.url.includes('cleared');
    const rawValueColumns: string[] = config.offer_dimensions?.length ? config.offer_dimensions : ['kW'];
    const valueColumns = isCleared ? ['price', 'quantity'] : rawValueColumns;

    const header = ['registration_id', 'registration_name', 'start_dttm', 'end_dttm', ...(isCleared ? [] : ['opt_out']), ...valueColumns];
    const blankValues = rawValueColumns.map(() => '');
    const rows = this.buildOffersIntervalRows(config, (start, end) => [
      config.registrationId,
      this.escapeRegistrationName(config.registrationName),
      start,
      end,
      ...(isCleared ? [] : ['false']),
      ...blankValues
    ]);
    return this.toCsvString([this.buildLabelRow(header), header, ...rows]);
  }

  private buildOffersIntervalRows(config: CsvTemplateConfig, rowFactory: (start: string, end: string) => string[]): string[][] {
    const { intervalFrequency, startDate, endDate, timeZone } = config;
    const current = moment.tz(startDate.format('YYYY-MM-DDTHH:mm:ss'), timeZone);
    const end = moment.tz(endDate.format('YYYY-MM-DDTHH:mm:ss'), timeZone);
    const rows: string[][] = [];
    while (current.isBefore(end)) {
      const intervalStart = current.clone();
      const intervalEnd = current.clone().add(intervalFrequency, 'minutes');
      if (intervalEnd.isAfter(end)) break;
      rows.push(rowFactory(intervalStart.format(), intervalEnd.format()));
      current.add(intervalFrequency, 'minutes');
    }
    return rows;
  }

  // ------------------------------------
  // BASELINES
  // ------------------------------------

  /**
   * Builds the Baselines CSV content.
   *
   * This is needed because baseline gate-closure information
   * must be loaded from the backend before the CSV rows are created (for the selected dates in the date-picker).
   */
  private buildBaselinesCsv$(config: CsvTemplateConfig): Observable<string> {
    return this.getBaselinesGateClosureInfoForCsvRange(config).pipe(
      map((gateClosedInfoResult) => {
        const header = ['registration_id', 'registration_name', 'start_dttm', 'end_dttm', 'baseline_value'];

        const rows = this.buildBaselineIntervalRows(
          config,
          (start, end) => [config.registrationId, this.escapeRegistrationName(config.registrationName), start, end, ''],
          gateClosedInfoResult
        );

        return this.toCsvString([this.buildLabelRow(header), header, ...rows]);
      })
    );
  }

  /**
   * Loads baseline data for the CSV selected date range and calculates
   * the gate-closure information from that data.
   *
   * This does not use the table cache because the table may be showing
   * a different week than the CSV range selected by the user.
   */
  private getBaselinesGateClosureInfoForCsvRange(config: CsvTemplateConfig): Observable<BaselineGateClosureInfoResult> {
    const regConfig = this.currentRegViewConfig();

    if (!regConfig) {
      return of({
        allClosed: false,
        startDate: null,
        endDate: null
      });
    }

    const selectedDatesISO = this.dateService.getISODate(
      {
        start: config.startDate,
        end: config.endDate
      },
      regConfig.timeZone
    );

    /**
     * Load baselines for the selected date range, not for the week currently
     * displayed in the table. The returned data is used only to determine
     * whether the CSV start date must be restricted by gate closures.
     */
    return this.baselineService.getBaselines(regConfig.registrationId, selectedDatesISO.start, selectedDatesISO.end).pipe(
      map((offers: BaselineResponse[]) => {
        const data: DataModel[] = this.dataModelService.getGridDataFromBaseline(offers, regConfig);

        return this.calculateBaselinesGateClosureInfo(data, regConfig.timeZone);
      }),
      catchError((error) => {
        console.error('Error loading baselines for CSV gate closure info', error);

        return of({
          allClosed: false,
          startDate: null,
          endDate: null
        });
      })
    );
  }

  /**
   * Calculates gate-closure information from baseline data.
   *
   * If there are no closed gates, startDate and endDate are null.
   * If there are closed gates, startDate is the first open interval after
   * the closed period.
   */
  private calculateBaselinesGateClosureInfo(offers: DataModel[], timeZone: string): BaselineGateClosureInfoResult {
    if (!offers.length) {
      return {
        allClosed: false,
        startDate: null,
        endDate: null
      };
    }

    const hasAnyClosedGate = offers.some((offer) => offer.gate_closed);
    const allClosed = offers.every((offer) => offer.gate_closed);

    if (!hasAnyClosedGate) {
      return {
        allClosed: false,
        startDate: null,
        endDate: null
      };
    }

    if (allClosed) {
      return {
        allClosed: true,
        startDate: null,
        endDate: null
      };
    }

    /**
     * Find the first interval that is NOT closed.
     * This represents the first valid date after the initial closed gate period.
     */
    const firstNotClosedIndex = offers.findIndex((offer) => !offer.gate_closed);

    let lastNotClosedIndex = -1;

    /**
     * Find the last interval that is NOT closed.
     */
    for (let i = offers.length - 1; i >= 0; i--) {
      if (!offers[i].gate_closed) {
        lastNotClosedIndex = i;
        break;
      }
    }

    return {
      allClosed: false,
      startDate: moment.utc(offers[firstNotClosedIndex].offer_start_dttm_utc).tz(timeZone),
      endDate: moment.utc(offers[lastNotClosedIndex].offer_start_dttm_utc).tz(timeZone)
    };
  }

  /**
   * Builds the CSV interval rows between the effective start and end dates.
   *
   * The effective range is calculated from the selected config range,
   * the current time, and possible gate-closure information.
   */
  private buildBaselineIntervalRows(
    config: CsvTemplateConfig,
    rowFactory: (start: string, end: string) => string[],
    gateClosureInfo?: BaselineGateClosureInfoResult
  ): string[][] {
    const { intervalFrequency, timeZone } = config;

    const effectiveRange = this.getBaselinesEffectiveCSVDateRange(config, gateClosureInfo);

    if (!effectiveRange) {
      return [];
    }

    const current = moment.tz(effectiveRange.startDate.format('YYYY-MM-DDTHH:mm:ss'), timeZone);

    const end = moment.tz(effectiveRange.endDate.format('YYYY-MM-DDTHH:mm:ss'), timeZone);

    const rows: string[][] = [];

    while (current.isBefore(end)) {
      const intervalStart = current.clone();
      const intervalEnd = current.clone().add(intervalFrequency, 'minutes');

      if (intervalEnd.isAfter(end)) {
        break;
      }

      rows.push(rowFactory(intervalStart.format(), intervalEnd.format()));
      current.add(intervalFrequency, 'minutes');
    }

    return rows;
  }

  /**
   * Calculates the final date range used to generate Baselines CSV rows.
   */
  private getBaselinesEffectiveCSVDateRange(
    config: CsvTemplateConfig,
    gateClosureInfo?: BaselineGateClosureInfoResult
  ): { startDate: moment.Moment; endDate: moment.Moment } | null {
    const { startDate, endDate, timeZone, intervalFrequency } = config;

    let effectiveStartDate = startDate.clone();
    const effectiveEndDate = endDate.clone();

    const now = moment.tz(timeZone);

    /**
     * Round "now" up to the next valid interval.
     * Example with 15 min frequency:
     * 13:40 -> 13:45
     * 13:45 -> 13:45
     * 13:46 -> 14:00
     */
    const roundedNow = now.clone().seconds(0).milliseconds(0);
    const minutes = roundedNow.minutes();
    const remainder = minutes % intervalFrequency;

    if (remainder !== 0) {
      roundedNow.add(intervalFrequency - remainder, 'minutes');
    }

    if (roundedNow.isAfter(effectiveStartDate)) {
      effectiveStartDate = roundedNow.clone();
    }

    if (gateClosureInfo?.startDate && gateClosureInfo.startDate.isAfter(effectiveStartDate)) {
      effectiveStartDate = gateClosureInfo.startDate.clone();
    }

    if (!effectiveStartDate.isBefore(effectiveEndDate)) {
      return null;
    }

    return {
      startDate: effectiveStartDate,
      endDate: effectiveEndDate
    };
  }

  // wrap the registration name in double quotes if it contains any comma (to distinguish from column separators)
  private escapeRegistrationName(registrationName: string): string {
    return registrationName && registrationName.includes(',') ? `"${registrationName}"` : registrationName;
  }

  private toCsvString(rows: string[][]): string {
    return rows.map((row) => row.join(',')).join('\n');
  }

  private buildFileName(registrationId: string, importType: BulkImportType): string {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(7);
    const typeLabel = importType === BulkImportType.Baselines ? 'baselines' : 'offers';
    return `csv_template_${typeLabel}_${registrationId}_${timestamp}_${randomId}`;
  }
}
